import React from 'react';
import ValidationElement from '../ValidationElement';
import { ariaLabel } from '../Generic';

export default class Radio extends ValidationElement {
  constructor(props) {
    super(props);

    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
      checked: props.checked,
      value: props.value,
      focus: props.focus,
      error: props.error,
      valid: props.valid,
      native: props.native,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.skipNext = false;

    this.radio = React.createRef();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.radio.current) {
      // The component has not mounted yet
      return;
    }
    if (this.state.checked !== nextProps.checked) {
      this.setState({ checked: nextProps.checked });
    }
  }

  /**
   * The concept of skipping an update was brought about by the unnatural way
   * people think a radio button should behave (this was found through a year's
   * worth of usability testing).
   *
   * Expected behavior:
   *   - a click/keypress on the radio button should toggle its checked value
   *   - key presses may be enter, space, and browser default
   *
   * When a mouse click event is fired we make a change to the value of the radio
   * button thus causing another change event. This second event we need to account
   * for and skip further processing.
   */
  skip(clicked) {
    if (this.skipNext) {
      this.skipNext = false;
      return true;
    }

    this.skipNext = clicked;
    return false;
  }

  /**
   * Update the value of the radio button
   */
  update(clicked = false) {
    const { name, value, ignoreDeselect } = this.props;
    const { checked } = this.state;

    if (this.skip(clicked)) {
      return;
    }
    if (ignoreDeselect && checked) {
      return;
    }

    const updatedValue = !checked ? value : '';
    this.setState(
      (prevState) => ({
        checked: !prevState.checked,
        value: updatedValue,
      }),
      () => {
        this.props.onUpdate({
          name,
          value: updatedValue,
          checked,
        });
      }
    );
  }

  /**
   * Handle the change event.
   */
  handleChange(event) {
    this.update();
  }

  /**
   * Handle the click event.
   */
  handleClick(event) {
    this.update(true);
  }

  /**
   * Handle the key press event.
   */
  handleKeyPress(event) {
    const allowedKeys = [' ', 'Enter'];
    if (allowedKeys.includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      this.update();
    }
  }

  /**
   * Handle the focus event.
   */
  handleFocus(event) {
    this.setState({ focus: true }, () => {
      super.handleFocus(event);
    });
  }

  /**
   * Handle the blur event.
   */
  handleBlur(event) {
    this.setState({ focus: false }, () => {
      super.handleBlur(event);
    });
  }

  /**
   * Execute validation checks on the value.
   */
  handleValidation(event) {
    const value = this.state.value;
    const errors = this.props.onError(
      value,
      this.constructor.errors.map((err) => ({
        code: err.code,
        valid: value ? err.func(value, this.props) : null,
        uid: this.state.uid,
      }))
    ) || [];

    this.setState({
      error: errors.some((x) => !x.valid),
      valid: errors.every((x) => x.valid),
    });
  }

  /**
   * Style classes applied to the wrapper.
   */
  divClass() {
    let klass = `${this.props.className || ''}`;

    if (!this.props.native) {
      klass += ' block';
    }

    if (this.props.children) {
      klass += ' extended';
    }

    if (this.props.disabled) {
      klass += ' disabled';
    }

    if (this.state.error) {
      klass += ' usa-input-error';
    }

    return klass.trim();
  }

  /**
   * Style classes applied to the label element.
   */
  labelClass() {
    let klass = '';

    if (this.state.error) {
      klass += ' usa-input-error-label';
    }

    if (this.state.checked) {
      klass += ' checked';
    }

    if (this.state.focus) {
      klass += ' usa-input-focus';
    }

    return klass.trim();
  }

  /**
   * Style classes applied to the input element.
   */
  inputClass() {
    let klass = '';

    if (this.state.focus) {
      klass += ' usa-input-focus';
    }

    if (this.state.valid) {
      klass += ' usa-input-success';
    }

    if (this.state.checked) {
      klass += ' selected';
    }

    return klass.trim();
  }

  render() {
    const speech = this.props.ariaLabel
      ? this.props.ariaLabel
      : `${this.props.label} for ${ariaLabel(this.radio.current)}`;
    if (this.props.native) {
      return (
        <div className={this.divClass()}>
          <input
            className={this.inputClass()}
            id={this.state.uid}
            name={this.state.uid}
            type="radio"
            ref={this.radio}
            disabled={this.props.disabled}
            readOnly={this.props.readonly}
            value={this.state.value}
            onChange={this.handleChange} // eslint-disable-line @typescript-eslint/unbound-method
            onKeyDown={this.handleKeyPress} // eslint-disable-line @typescript-eslint/unbound-method
            onFocus={this.handleFocus} // eslint-disable-line @typescript-eslint/unbound-method
            onBlur={this.handleBlur} // eslint-disable-line @typescript-eslint/unbound-method
            checked={this.state.checked}
            aria-label={speech}
            aria-checked={this.state.checked}
          />
          <label htmlFor={this.state.uid}>
            {this.props.children}
            <span>{this.props.label}</span>
          </label>
        </div>
      );
    }

    return (
      <div className={this.divClass()}>
        <input
          className={this.inputClass()}
          id={this.state.uid}
          name={this.state.uid}
          type="radio"
          ref={this.radio}
          disabled={this.props.disabled}
          readOnly={this.props.readonly}
          value={this.state.value}
          onChange={this.handleChange} // eslint-disable-line @typescript-eslint/unbound-method
          onClick={this.handleClick} // eslint-disable-line @typescript-eslint/unbound-method
          onKeyDown={this.handleKeyPress} // eslint-disable-line @typescript-eslint/unbound-method
          onFocus={this.handleFocus} // eslint-disable-line @typescript-eslint/unbound-method
          onBlur={this.handleBlur} // eslint-disable-line @typescript-eslint/unbound-method
          checked={this.state.checked}
          aria-label={speech}
          aria-checked={this.state.checked}
        />
        <label className={this.labelClass()} htmlFor={this.state.uid}>
          {this.props.children}
          <span>{this.props.label}</span>
        </label>
      </div>
    );
  }
}

Radio.defaultProps = {
  name: 'radio_input',
  checked: false,
  disabled: false,
  valued: '',
  focus: false,
  error: false,
  valid: false,
  native: false,
  ariaLabel: '',
  role: 'radio',
  tabindex: '',
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
};

Radio.errors = [];
